import { useRef, useCallback } from 'react';
import { useStateContext } from '../context/StateContext';

export function useVoLoop(audioContextRef, gainNodeRef) {

  const {customLog} = useStateContext() 
  // Refs to track state
  const currentVoSourceRef = useRef(null);
  const voiceOverLoopTimeoutRef = useRef(null);
  const preludeEndTimeoutRef = useRef(null);
  const voBufferRef = useRef(null);
  const isPausedRef = useRef(false);
  const startTimeRef = useRef(0);
  const pausedAtRef = useRef(0);
  const currentLoopRef = useRef(0);
  const loopCountTargetRef = useRef(3); // Default number of loops
  const initialDelaySeconds = 8; // Initial delay before first loop
  
  // Store all timeouts to ensure they can be cleared
  const allTimeoutsRef = useRef([]);

  // Helper function to track timeouts
  const trackTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      // Remove this timeout from the tracking array
      allTimeoutsRef.current = allTimeoutsRef.current.filter(id => id !== timeoutId);
      // Execute the callback
      callback();
    }, delay);
    
    // Add to tracking array
    allTimeoutsRef.current.push(timeoutId);
    return timeoutId;
  }, []);

  // Create and connect gain node once
  const getOrCreateGainNode = useCallback(() => {
    if (!gainNodeRef?.current) {
      const context = audioContextRef.current;
      if (!context) return null;
      
      // Create a new gain node if one wasn't provided
      const newGainNode = context.createGain();
      newGainNode.gain.value = 0.7; // default volume
      newGainNode.connect(context.destination);
      
      // If gainNodeRef is provided, update it
      if (gainNodeRef) {
        gainNodeRef.current = newGainNode;
      }
      
      return newGainNode;
    }
    return gainNodeRef.current;
  }, [audioContextRef, gainNodeRef]);

  // Clean up voice over resources
  const cleanupVoiceOver = useCallback(() => {

      customLog('Cleanup voiceover called. Current state:', {
      hasLoopTimeout: !!voiceOverLoopTimeoutRef.current,
      hasPreludeEndTimeout: !!preludeEndTimeoutRef.current,
      hasVoiceOver: !!currentVoSourceRef.current,
      pendingTimeouts: allTimeoutsRef.current.length
    });

    // Clear the main loop timeout
    if (voiceOverLoopTimeoutRef.current) {
      clearTimeout(voiceOverLoopTimeoutRef.current);
      voiceOverLoopTimeoutRef.current = null;
    }
    
    // Clear the prelude end timeout
    if (preludeEndTimeoutRef.current) {
      clearTimeout(preludeEndTimeoutRef.current);
      preludeEndTimeoutRef.current = null;
    }
    
    // Clear all tracked timeouts
    allTimeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    allTimeoutsRef.current = [];
    
    // Stop the current audio source if it exists
    if (currentVoSourceRef.current) {
      try {
        currentVoSourceRef.current.stop();
      } catch (error) {
        customLog("Error stopping voice over source:", error);
      }
      currentVoSourceRef.current = null;
    }
    
    // Reset state
    currentLoopRef.current = 0;
    isPausedRef.current = false;
    
    customLog('Voice over cleanup complete');
  }, []);

  // Calculate voice over loop parameters
  const calculateLoopParams = useCallback((voBuffer, preludeLength, currentTime = 0) => {
    if (!voBuffer || !preludeLength) {
      customLog('Cannot calculate loop params - missing buffer or prelude length');
      return { numberOfLoops: 0, loopInterval: 0 };
    }

    customLog('Calculating loop parameters:', {
      bufferDuration: voBuffer.duration,
      preludeLength,
      currentTime
    });

    const voiceOverDuration = voBuffer.duration;
    
    // Loop interval is voice over duration plus 8 second gap
    const loopInterval = voiceOverDuration + initialDelaySeconds;
    
    customLog('Voice over duration and loop interval:', {
      voiceOverDuration,
      gapDuration: initialDelaySeconds,
      totalLoopInterval: loopInterval
    });
    
    // Calculate remaining prelude time if we're already at a specific time
    const remainingPreludeTime = preludeLength - currentTime;
    
    // Calculate the maximum number of loops that can fit in the remaining prelude time
    // If we're at the beginning (currentTime = 0), we need to account for the initial delay
    const effectiveTime = currentTime === 0 
      ? remainingPreludeTime - initialDelaySeconds 
      : remainingPreludeTime;
    
    customLog('Time calculations:', {
      remainingPreludeTime,
      effectiveTime,
      initialDelay: initialDelaySeconds
    });
    
    // Calculate how many loops can fit
    let numberOfLoops = Math.floor(effectiveTime / loopInterval);
    
    customLog('Initial loop count:', {
      numberOfLoops,
      loopInterval,
      calculation: `${effectiveTime} / ${loopInterval} = ${effectiveTime/loopInterval}`
    });
    
    // If there's not enough time for even one loop, don't force it
    if (effectiveTime < voiceOverDuration) {
      numberOfLoops = 0;
      customLog('Not enough time for even one loop');
    }
    
    // Check if the last voiceover would extend beyond the prelude end
    const lastLoopStartTime = currentTime + initialDelaySeconds + (numberOfLoops - 1) * loopInterval;
    const lastLoopEndTime = lastLoopStartTime + voiceOverDuration;
    
    customLog('Last loop timing:', {
      lastLoopStartTime,
      lastLoopEndTime,
      wouldExtendBeyondPrelude: lastLoopEndTime > preludeLength
    });
    
    // If the last loop would extend beyond the prelude, don't include it
    if (lastLoopEndTime > preludeLength && numberOfLoops > 0) {
      customLog('Last voiceover would extend beyond prelude end, reducing loop count');
      numberOfLoops = Math.max(0, numberOfLoops - 1);
    }

    const result = {
      voiceOverDuration,
      loopInterval,
      numberOfLoops,
      totalDuration: numberOfLoops * loopInterval,
      lastLoopEndTime,
      remainingPreludeTime
    };
    
    customLog('Final loop parameters:', result);
    
    return result;
  }, [initialDelaySeconds]);

  // Setup voice over with loop functionality
  const setupVoiceOver = useCallback((voBuffer, preludeLength, volume = 0.7, currentTime = 0) => {
    if (!voBuffer || !preludeLength || !audioContextRef.current) {
      customLog('Voice-over setup conditions not met:', { 
        hasBuffer: !!voBuffer, 
        preludeLength, 
        hasContext: !!audioContextRef.current 
      });
      return;
    }

    // Clean up any existing voice over first
    cleanupVoiceOver();
    
    // Store the buffer for potential resume operations
    voBufferRef.current = voBuffer;
    isPausedRef.current = false;
    pausedAtRef.current = 0;
    startTimeRef.current = audioContextRef.current.currentTime;
    
    // Calculate loop parameters based on current time within the prelude
    const { numberOfLoops, loopInterval } = calculateLoopParams(voBuffer, preludeLength, currentTime);
    loopCountTargetRef.current = numberOfLoops;
    
    customLog('Voice-over loop setup:', {
      voiceOverDuration: voBuffer.duration,
      loopInterval,
      numberOfLoops,
      preludeLength,
      currentTime,
      remainingPreludeTime: preludeLength - currentTime
    });

    // If no loops can fit, don't bother setting up
    if (numberOfLoops <= 0) {
      customLog('No loops can fit in the remaining prelude time');
      return;
    }

    // Start the loop sequence
    let currentLoop = 0;

    const playLoop = () => {
      // Check if we should stop the loop
      if (currentLoop >= numberOfLoops || isPausedRef.current) {
        customLog('Voice-over loops complete or paused');
        return;
      }
      
      // Double-check that we're still playing
      if (!audioContextRef.current) {
        customLog('Audio context no longer available, stopping voice over');
        return;
      }
      
      customLog(`Starting voice-over loop ${currentLoop + 1}/${numberOfLoops}`);
      
      // Get or create gain node
      const gainNode = getOrCreateGainNode();
      if (!gainNode) return;
      
      // Set volume
      gainNode.gain.setValueAtTime(volume, audioContextRef.current.currentTime);
      
      // Create and set up source
      const source = audioContextRef.current.createBufferSource();
      source.buffer = voBuffer;
      source.connect(gainNode);
      
      // Store current source
      currentVoSourceRef.current = source;
      currentLoopRef.current = currentLoop;
      
      try {
        // Start the source immediately
        source.start();
        customLog(`Voice over loop ${currentLoop + 1} started`);
        
        // Schedule the next loop
        currentLoop++;
        
        if (currentLoop < numberOfLoops) {
          // Clear any existing timeout
          if (voiceOverLoopTimeoutRef.current) {
            clearTimeout(voiceOverLoopTimeoutRef.current);
            voiceOverLoopTimeoutRef.current = null;
          }
          
          // Calculate the delay for the next loop
          const nextLoopDelay = (voBuffer.duration + initialDelaySeconds) * 1000;
          customLog(`Scheduling next loop (${currentLoop}/${numberOfLoops}) in ${nextLoopDelay/1000}s`, {
            voiceDuration: voBuffer.duration,
            gapDuration: initialDelaySeconds,
            totalDelay: nextLoopDelay
          });
          
          // Schedule next loop after the current one finishes plus the gap
          voiceOverLoopTimeoutRef.current = trackTimeout(() => {
            customLog(`Next loop timeout fired for loop ${currentLoop}/${numberOfLoops}`);
            // Only proceed if we're not paused and the component is still mounted
            if (!isPausedRef.current && audioContextRef.current) {
              playLoop();
            } else {
              customLog('Skipping next loop because:', {
                isPaused: isPausedRef.current,
                hasContext: !!audioContextRef.current
              });
            }
          }, nextLoopDelay);
        }
      } catch (error) {
        customLog("Error starting voice over:", error);
      }
    };

    // Calculate initial delay based on current position
    // If we're at the beginning, use the full initial delay
    // If we're seeking within the prelude, calculate the appropriate delay
    let initialDelay = initialDelaySeconds;
    
    if (currentTime > 0) {
      // Calculate where we are in the loop cycle
      const cyclePosition = currentTime % loopInterval;
      
      // If we're in the initial delay period
      if (cyclePosition < initialDelaySeconds) {
        initialDelay = initialDelaySeconds - cyclePosition;
      } else {
        // We're in the middle of a loop or just after one
        // Start the next loop after the appropriate gap
        const timeIntoVoiceover = cyclePosition - initialDelaySeconds;
        
        if (timeIntoVoiceover < voBuffer.duration) {
          // We're in the middle of a voiceover, skip this one and wait for the next
          initialDelay = voBuffer.duration - timeIntoVoiceover + initialDelaySeconds;
        } else {
          // We're in the gap between voiceovers
          initialDelay = loopInterval - cyclePosition;
        }
      }
    }
    
    // If the initial delay is too long (more than half the remaining prelude time),
    // don't bother playing the voice over
    if (initialDelay > (preludeLength - currentTime) / 2) {
      customLog(`Initial delay (${initialDelay}s) is too long compared to remaining prelude time (${preludeLength - currentTime}s), skipping voice over`);
      return;
    }
    
    customLog(`Starting first voice-over loop with ${initialDelay}s delay`);
    
    // Schedule the first loop with tracking
    voiceOverLoopTimeoutRef.current = trackTimeout(() => {
      // Check if we're still playing before executing the loop
      if (!isPausedRef.current && audioContextRef.current) {
        playLoop();
      } else {
        customLog('Skipping scheduled loop because playback was stopped');
      }
    }, initialDelay * 1000);
    
    customLog(`First loop scheduled to start in ${initialDelay}s`);
    
    // Set timeout to stop voice over at end of prelude
    preludeEndTimeoutRef.current = trackTimeout(() => {
      customLog('Stopping voice-over at prelude end');
      cleanupVoiceOver();
    }, (preludeLength - currentTime) * 1000);
    
    customLog(`Prelude end timeout set for ${preludeLength - currentTime}s from now`);
    
    return {
      numberOfLoops,
      loopInterval,
      preludeLength,
      initialDelay
    };
  }, [audioContextRef, calculateLoopParams, cleanupVoiceOver, getOrCreateGainNode, initialDelaySeconds, trackTimeout]);

  // Play a voice over buffer
  const play = useCallback((voBuffer, offset = 0, loopIteration = 0, volume = 0.7, preludeLength = null, currentTime = 0) => {
    if (!audioContextRef.current || !voBuffer) {
      customLog('Play conditions not met:', { 
        hasContext: !!audioContextRef.current, 
        hasBuffer: !!voBuffer 
      });
      return;
    }
    
    // If prelude length is provided, use the loop setup
    if (preludeLength) {
      return setupVoiceOver(voBuffer, preludeLength, volume, currentTime || offset);
    }
    
    // Otherwise, just play the voice over once
    cleanupVoiceOver();
    
    voBufferRef.current = voBuffer;
    
    const gainNode = getOrCreateGainNode();
    if (!gainNode) return;
    
    gainNode.gain.setValueAtTime(volume, audioContextRef.current.currentTime);
    
    const source = audioContextRef.current.createBufferSource();
    source.buffer = voBuffer;
    source.loop = false;
    source.connect(gainNode);
    
    currentVoSourceRef.current = source;
    currentLoopRef.current = loopIteration;
    isPausedRef.current = false;
    pausedAtRef.current = 0;
    startTimeRef.current = audioContextRef.current.currentTime - offset;
    
    try {
      source.start(0, offset);
    } catch (error) {
      customLog("Error starting voice over:", error);
    }
    
    return {
      source,
      duration: voBuffer.duration
    };
  }, [audioContextRef, cleanupVoiceOver, getOrCreateGainNode, setupVoiceOver]);

  // Pause voice over playback
  const pause = useCallback(() => {
    if (currentVoSourceRef.current && !isPausedRef.current && audioContextRef.current) {
      try {
        const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
        pausedAtRef.current = elapsed;
        currentVoSourceRef.current.stop();
        currentVoSourceRef.current = null;
      } catch (error) {
        customLog("Error stopping voice over source during pause:", error);
      }
      isPausedRef.current = true;
      
      // Clear any pending loop timeouts
      if (voiceOverLoopTimeoutRef.current) {
        clearTimeout(voiceOverLoopTimeoutRef.current);
        voiceOverLoopTimeoutRef.current = null;
      }
    }
  }, [audioContextRef]);

  // Resume voice over playback
  const resume = useCallback((volume = 0.7) => {
    if (isPausedRef.current && voBufferRef.current) {
      play(voBufferRef.current, pausedAtRef.current, currentLoopRef.current, volume);
      pausedAtRef.current = 0;
    }
  }, [play]);

  // Stop voice over playback completely
  const stop = useCallback(() => {
    cleanupVoiceOver();
  }, [cleanupVoiceOver]);

  // Set voice over volume dynamically
  const setVolume = useCallback((volume) => {
    const gainNode = gainNodeRef?.current;
    if (gainNode && audioContextRef.current) {
      gainNode.gain.setValueAtTime(volume, audioContextRef.current.currentTime);
    }
  }, [audioContextRef, gainNodeRef]);

  return { 
    play, 
    pause, 
    resume, 
    stop, 
    setVolume, 
    setupVoiceOver, 
    cleanupVoiceOver,
    calculateLoopParams
  };
}
